import React from "react";
import ReactDOM from "react-dom";
import { I18nextProvider } from "react-i18next";

// isomorphic-fetch overrides the global fetch. Importing this way allows it to also be mocked with jest-fetch-mock
import "isomorphic-fetch";

// Keep this confirmation in the Beacon layer: importing Operator providers here reconnects the
// global fetch module through BaseDashboardComponent -> FetchErrorModal -> fetch, creating a
// high-fanout async cycle that caused Cypress preprocessing stalls and excessive memory use in PR #137463.
// This path remains human-operated.
import {
  AlertModal,
  AlertModalBody,
  AlertModalButton,
  AlertModalFooter,
  AlertModalHeading,
} from "@braze/beacon-commons";

// eslint-disable-next-line local-eslint-rules/no-direct-dashboard-config-access
import config from "@lib/dashboardConfig";
import { FetchRequestError } from "@lib/errors";

// eslint-disable-next-line local-eslint-rules/enforce-namespace-modularity
import { PlatformThemeProvider } from "@components/PlatformThemeProvider";

import i18n from "../../i18n";
import { allowRequest } from "./utils/allowRequest";
import { CANCELED_BY_USER } from "./constants";

export const impersonationFetch = async (url: string, fetchOptions: RequestInit) => {
  const confirmOnImpersonation = config?.i && !config?.roi && config?.features?.impersonation_confirmation_modal;
  if (!confirmOnImpersonation) {
    return fetch(url, fetchOptions);
  }

  if (allowRequest(url, fetchOptions)) {
    return fetch(url, fetchOptions);
  }

  console.warn("[RequestInterceptor]", "fetch params", url, fetchOptions);
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.width = "100vw";
  container.style.height = "100vh";
  container.style.zIndex = "99999999";
  // render a confirmation modal to ensure the developer's intent is to modify production data
  const confirmationPromise = new Promise<boolean>((resolve) => {
    ReactDOM.render(
      <PlatformThemeProvider>
        <I18nextProvider i18n={i18n}>
          <AlertModal
            className="confirmation-modal"
            isOpen
            intent="warning"
            size="sm"
            toggle={() => resolve(false)}
            ignoreOutsideClick
            hideCloseButton
            zIndex={99999999}
          >
            <AlertModalHeading>You are making a change in a customer dashboard</AlertModalHeading>
            <AlertModalBody className="confirmation-modal_body">
              Be careful as some changes <b>cannot</b> be undone. Are you sure you want to make this change?
              <br />
              <br />
              <i>(endpoint URL: {url})</i>
            </AlertModalBody>
            <AlertModalFooter className="confirmation-modal_footer">
              <AlertModalFooter aligner side="right">
                <AlertModalButton
                  className="confirmation-modal_cancel-button bcl-ignore-click-outside"
                  variant="secondary"
                  onClick={() => resolve(false)}
                >
                  Cancel
                </AlertModalButton>
                <AlertModalButton
                  className="confirmation-modal_ok-button bcl-ignore-click-outside"
                  variant="primary"
                  onClick={() => resolve(true)}
                >
                  OK
                </AlertModalButton>
              </AlertModalFooter>
            </AlertModalFooter>
          </AlertModal>
        </I18nextProvider>
      </PlatformThemeProvider>,
      container
    );
  });

  const confirmed = await confirmationPromise;
  ReactDOM.unmountComponentAtNode(container);

  if (confirmed) {
    return fetch(url, fetchOptions);
  }
  throw new FetchRequestError(500, CANCELED_BY_USER);
};
